import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  PlusIcon, 
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { getUsers, type User } from '@/api/usersApi';
import { getRoles } from '@/api/rolesApi';
import toast from 'react-hot-toast';

const SuperAdminsPage: React.FC = () => {
  const { user } = useAuth();
  const [schoolAdmins, setSchoolAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [schoolAdminRoleId, setSchoolAdminRoleId] = useState<string>('');
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (schoolAdminRoleId) {
      fetchSchoolAdmins();
    }
  }, [schoolAdminRoleId, searchQuery]);

  const fetchRoles = async () => {
    try {
      const response = await getRoles();
      const roles = response.data; // Extract the data array
      const schoolAdminRole = roles.find(role => role.name === 'School Admin');
      if (schoolAdminRole) {
        setSchoolAdminRoleId(schoolAdminRole.id);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error('Failed to fetch roles');
    }
  };

  const fetchSchoolAdmins = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        roleId: schoolAdminRoleId, // Filter by School Admin role
        search: searchQuery.trim() || undefined,
        page: 1,
        limit: 50 // Show more school admins since there are fewer of them
      });
      
      setSchoolAdmins(response.users);
      
      // Calculate stats
      const totalAdmins = response.users.length;
      const activeAdmins = response.users.filter(u => u.status === 'Active').length;
      const inactiveAdmins = totalAdmins - activeAdmins;
      
      setStats({
        total: totalAdmins,
        active: activeAdmins,
        inactive: inactiveAdmins
      });
      
    } catch (error) {
      console.error('Failed to fetch school admins:', error);
      toast.error('Failed to fetch school administrators');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Never';
    return new Date(lastLogin).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleViewAdmin = (admin: User) => {
    toast.success(`Viewing ${admin.fullName} details`);
    // TODO: Implement view modal
  };

  const handleEditAdmin = (admin: User) => {
    toast.success(`Edit ${admin.fullName} functionality`);
    // TODO: Implement edit modal
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">School Administrators</h1>
          <p className="text-gray-600 mt-1">
            Manage school administrators across all institutions
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total School Admins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <ShieldCheckIcon className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search school administrators..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>
      </div>

      {/* School Admins Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="dashboard-card animate-pulse">
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : schoolAdmins.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schoolAdmins.map((admin) => (
            <motion.div
              key={admin.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="dashboard-card hover:shadow-lg transition-shadow"
            >
              {/* Admin Info */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      {admin.firstName[0]}{admin.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {admin.fullName}
                    </h3>
                    <p className="text-sm text-gray-500">{admin.institution}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  admin.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {admin.status}
                </span>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>{admin.email}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <BuildingOfficeIcon className="h-4 w-4" />
                  <span>{admin.institution}</span>
                </div>
                {admin.lastLogin && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4" />
                    <span>Last login: {formatLastLogin(admin.lastLogin)}</span>
                  </div>
                )}
              </div>

              {/* Member Since */}
              <div className="text-xs text-gray-500 mb-4">
                Member since {formatDate(admin.createdAt)}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  Created by {admin.createdBy}
                </span>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleViewAdmin(admin)}
                    className="btn btn-ghost text-xs"
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleEditAdmin(admin)}
                    className="btn btn-ghost text-xs"
                    title="Edit Admin"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="dashboard-card">
          <div className="text-center py-12">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No School Administrators Found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery ? 'Try adjusting your search terms.' : 'No school administrators have been created yet.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminsPage;